import { getCatalogEditorModelPath } from "@/v2-backoffice-client/feature/configuration/feature.configuration.constants"
import type { JsonSchema } from "@/v2-backoffice-client/feature/configuration/feature.configuration.types"

/**
 * JSON Schema for a nav layer's Configuration blob.
 *
 * Source of truth is the runtime's own parser — `NavLayerConfiguration` in
 * `libs/backends/v2-layer-data` — which reads `queryType` and then decodes
 * `parameters` into the matching struct. A blob it cannot decode is dropped
 * with a row error, so the nav layer simply stops answering lookups; keep
 * this schema in step with that struct.
 */

const FEATURE_LAYER_PARAMETERS: JsonSchema = {
  type: "object",
  description: "Query parameters for an ArcGIS FeatureLayer lookup.",
  properties: {
    distance: {
      type: "number",
      description: "Search radius around the clicked point.",
    },
    units: {
      type: "string",
      description: "Unit `distance` is expressed in.",
      enum: [
        "meters",
        "feet",
        "kilometers",
        "miles",
        "nautical-miles",
        "yards",
      ],
    },
    returnGeometry: {
      type: "boolean",
      description:
        "Return the matched feature's geometry, not just its fields.",
    },
    where: {
      type: "string",
      description: "SQL filter applied on top of the spatial query.",
    },
    outFields: {
      type: "array",
      description: 'Fields to return. `["*"]` for all.',
      items: { type: "string" },
    },
  },
}

const OGC_FEATURE_LAYER_PARAMETERS: JsonSchema = {
  type: "object",
  description: "Query parameters for an OGC API - Features lookup.",
  properties: {
    apiKey: { type: "string", description: "Key sent with every request." },
    bboxCrs: {
      type: "string",
      description: "CRS URI the bounding box is expressed in.",
    },
    crs: {
      type: "string",
      description: "CRS URI the response geometry is requested in.",
    },
    f: { type: "string", description: "Response format, e.g. `json`." },
  },
}

const NAV_LAYER_CONFIGURATION_SCHEMA: JsonSchema = {
  $schema: "http://json-schema.org/draft-07/schema#",
  type: "object",
  title: "Nav layer configuration",
  description:
    "A nav layer resolves a map click to a real-world object (a cadastre unit, a building). It is a lookup service, not something drawn on the map.",
  properties: {
    url: {
      type: "string",
      description: "Service endpoint the lookup queries.",
    },
    type: {
      type: "string",
      description: "The object type a hit resolves to.",
    },
    queryType: {
      type: "string",
      description: "Which client the runtime queries the service with.",
      enum: ["FeatureLayer", "OGCFeatureLayer"],
    },
    featureIdentifier: {
      type: "string",
      description:
        "Field on the returned feature holding the identifier the platform keys the object by.",
    },
    externalIdentifier: {
      type: "string",
      description: "Field holding the source system's own identifier.",
    },
    externalSourceCode: {
      type: "string",
      description: "Which source system `externalIdentifier` belongs to.",
    },
    isDefault: {
      type: "boolean",
      description:
        "The nav layer a click resolves against when several cover the same point.",
    },
    priorityKey: {
      type: "string",
      description: "Tie-breaker among nav layers covering the same point.",
    },
    parameters: {
      description: "Shape follows `queryType`.",
      anyOf: [FEATURE_LAYER_PARAMETERS, OGC_FEATURE_LAYER_PARAMETERS],
    },
  },
  required: ["url", "type", "queryType", "featureIdentifier", "parameters"],
}

const CONFIGURATION_MODEL_NAME = "navlayer.configuration.json"

/** The nav layer catalog's contribution to the shared Monaco registry. */
export const NAV_LAYER_EDITOR_SCHEMAS: Record<string, JsonSchema> = {
  [CONFIGURATION_MODEL_NAME]: NAV_LAYER_CONFIGURATION_SCHEMA,
}

export function getNavLayerEditorModelPath(): string {
  return getCatalogEditorModelPath(CONFIGURATION_MODEL_NAME)
}

/**
 * What a new nav layer's editor is seeded with: every required key, so the
 * operator fills in a shape the runtime can already parse rather than
 * starting from an empty object the schema immediately flags.
 */
export const NAV_LAYER_CREATE_CONFIGURATION = JSON.stringify(
  {
    url: "",
    type: "",
    queryType: "FeatureLayer",
    featureIdentifier: "",
    parameters: { outFields: ["*"], returnGeometry: true },
  },
  null,
  2,
)
